import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import RegionSelect from '@/components/base/RegionSelect';

function translateError(message: string): string {
  if (message.includes('already registered')) {
    return '이미 가입된 이메일이에요. 로그인해 주세요.';
  }
  if (message.includes('password')) {
    return '비밀번호는 6자 이상이어야 해요.';
  }
  if (message.includes('Email not confirmed')) {
    return '이메일 인증이 켜져 있어요. Supabase 대시보드에서 "Confirm email"을 끄거나, 관리자에게 문의하세요.';
  }
  return '회원가입에 실패했어요. 잠시 후 다시 시도해 주세요.';
}

export default function Signup() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'owner' | 'teacher'>('teacher');
  const [academyName, setAcademyName] = useState('');
  const [address, setAddress] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<{ hasSession: boolean } | null>(null);
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');

    if (!address.trim()) {
      setError('지역(시/도, 시/군/구)을 선택해 주세요.');
      return;
    }
    if (password.length < 6) {
      setError('비밀번호는 6자 이상이어야 해요.');
      return;
    }

    setLoading(true);

    const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          full_name: name,
          role,
          academy_name: academyName,
          address,
        },
      },
    });

    setLoading(false);

    if (signUpError) {
      setError(translateError(signUpError.message));
      return;
    }

    // 가입 성공 → 성공 안내 화면 표시
    // (이메일 인증이 꺼져 있으면 세션이 바로 생기고, 켜져 있으면 인증 후 로그인)
    setSuccess({ hasSession: !!signUpData.session });
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-50 px-4 py-10">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-background-200 bg-white p-6 text-center md:p-8">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-checkbox-circle-line text-4xl"></i>
            </div>
            <h1 className="mt-5 text-2xl font-bold text-foreground-950">
              회원가입이 완료되었어요!
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              {success.hasSession ? (
                <>
                  가입 신청이 정상적으로 접수됐어요.
                  <br />
                  원장님 또는 시스템 운영자의 <strong>승인</strong>을 받으면 모든 기능을 이용할
                  수 있어요.
                </>
              ) : (
                <>
                  가입 신청이 정상적으로 접수됐어요.
                  <br />
                  <strong>{email}</strong> 으로 인증 메일을 보냈어요. 메일함에서 인증을 완료한 뒤
                  로그인해 주세요.
                </>
              )}
            </p>

            <button
              onClick={() => navigate(success.hasSession ? '/dashboard' : '/login')}
              className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
            >
              {success.hasSession ? '확인 (승인 대기 화면으로)' : '로그인하러 가기'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background-50 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-500 text-background-50">
            <i className="ri-graduation-cap-line text-3xl"></i>
          </div>
          <h1 className="mt-4 text-2xl font-bold text-foreground-950">계정 만들기</h1>
          <p className="mt-1 text-sm text-foreground-500">
            가입 신청 후 승인을 받으면 이용할 수 있어요
          </p>
        </div>

        <div className="rounded-2xl border border-background-200 bg-white p-6 md:p-8">
          <div className="mb-4 flex items-start gap-2.5 rounded-md bg-primary-100/70 px-3 py-2.5 text-xs leading-relaxed text-primary-800">
            <i className="ri-information-line mt-0.5 text-base"></i>
            <span>
              <strong className="font-semibold">원장</strong>은 시스템 운영자의 승인,
              <strong className="font-semibold"> 선생님</strong>은 해당 학원 원장님 또는 운영자의 승인이 필요해요.
              승인 전에는 로그인해도 대기 화면만 보여요.
            </span>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground-700">이름</label>
              <input
                type="text"
                name="name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="홍길동"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground-700">
                회원 유형
              </label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setRole('owner')}
                  className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium transition-colors ${
                    role === 'owner'
                      ? 'border-primary-500 bg-primary-100 text-primary-700'
                      : 'border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-store-2-line"></i>
                  원장
                </button>
                <button
                  type="button"
                  onClick={() => setRole('teacher')}
                  className={`flex cursor-pointer items-center justify-center gap-2 rounded-md border px-3 py-2.5 text-sm font-medium transition-colors ${
                    role === 'teacher'
                      ? 'border-primary-500 bg-primary-100 text-primary-700'
                      : 'border-background-300 bg-background-50 text-foreground-600 hover:bg-background-100'
                  }`}
                >
                  <i className="ri-user-line"></i>
                  선생님
                </button>
              </div>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground-700">학원명</label>
              <input
                type="text"
                name="academy_name"
                required
                value={academyName}
                onChange={(e) => setAcademyName(e.target.value)}
                placeholder="예: 성서 수학학원"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
              />
              <p className="mt-1 text-xs text-foreground-400">
                선생님은 원장님이 등록한 학원명과 <strong>정확히 동일하게</strong> 입력해야 해요.
              </p>
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground-700">지역</label>
              <RegionSelect value={address} onChange={setAddress} />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground-700">이메일</label>
              <input
                type="email"
                name="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@example.com"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-foreground-700">
                비밀번호
              </label>
              <input
                type="password"
                name="password"
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="6자 이상 입력하세요"
                className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-200"
              />
            </div>

            {error && (
              <div className="rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-800">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {loading ? '가입 중...' : '회원가입'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-foreground-500">
            이미 계정이 있으신가요?{' '}
            <Link to="/login" className="font-semibold text-primary-600 hover:text-primary-700">
              로그인
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}